import React from "react";
import _ from 'lodash';
import moment from 'moment-timezone';

import CreditLineItemPanel from './CreditLineItemPanel';
import CreditRefundPanel from "./CreditRefundPanel";
import CreditIncreasePanel from './CreditIncreasePanel';
import CreditWriteOffPanel from './CreditWriteOffPanel';

import { queryCreateCredit } from "../../../Queries";

import Field from "../../Fields/Field";
import InputSelectField from "../../Fields/InputSelectField";
import InputTextField from '../../Fields/InputTextField';
import InputDateField from '../../Fields/InputDateField';

import {
    convertUnixMsToDateInTimezone,
    getCurrentDateInTimezone
} from '../../../Utils/date';

import ActionButton from '../../Common/ActionButton';
import featureFlags from '../../../featureFlags';

import './CreditPanel.scss';

const creditQueryFields = [
    'id',
    'readable_id',
    'amount',
    'balance',
    'created_at',
    'subscription_id',
    'notes',
    'application_type',
    {
        'payment_meta': [
            'check_number'
        ]
    },
    {
        'credit_entries': [
            'id',
            'type',
            'amount',
            'amount_tax_1',
            'amount_tax_2',
            'amount_tax_3',
            'amount_tax_4',
            'application_date',
            'created_at'
        ]
    },
    {
        'subscription': [
            'id',
            {
                'payor': [
                    'readable_id',
                    'billing_firstname',
                    'billing_lastname'
                ]
            }
        ]
    }
];

class CreditPanel extends React.Component {
    state = {
        credit: null,
        applicationDate: null,
        creditSaveInProgress: false,
        refundMode: false,
        increaseMode: false,
        writeOffMode: false,
    };

    subscriptionId;

    constructor() {
        super(...arguments);
        const { config } = this.props;

        this.state = {
            ...this.state,
            ...config.initialState,
        };

        const urlSearchParams = new URLSearchParams(window.location.search);
        this.subscriptionId = urlSearchParams.get('subscriptionId');
    }

    get fieldConfiguration() {
        return this.props.config.fieldConfiguration;
    }

    componentDidMount() {
        if (featureFlags.PAYOR_SEARCH && this.subscriptionId) {
            const credit = _.cloneDeep(this.state.credit)
            credit.fields['subscription_id'].value = this.subscriptionId;

            this.setState({ credit })
        }
    }

    storeCreditData = (inputId) => {
        return (value) => {
            const credit = _.cloneDeep(this.state.credit);

            switch (inputId) {
                case 'applicationDate':
                    this.setState({ applicationDate: value });
                    break;
                case 'payment_type': {
                    credit.fields.payment_type.value = value;

                    let { applicationDate } = this.state;
                    if (value === 'Carryover Credit') {
                        credit.fields.application_type.value = 'CARRYOVER_CREDIT';
                        applicationDate = null;
                    } else if (value === 'Discount Credit') {
                        credit.fields.application_type.value = 'DISCOUNT_CREDIT';
                        applicationDate = null;
                    } else if (['CARRYOVER_CREDIT', 'DISCOUNT_CREDIT'].includes(credit.fields.application_type.value)) {
                        credit.fields.application_type.value = 'APPLY_CASH';
                        applicationDate = new Date();
                    }

                    this.setState({ credit, applicationDate });
                    break;
                }
                default:
                    credit.fields[inputId].value = value;
                    this.setState({ credit });
            }
        };
    };

    handleSaveCreditClick = async () => {
        this.setState({
            creditSaveInProgress: true,
        });

        const {
            credit,
            applicationDate,
        } = this.state;

        let creditId = credit.fields.id.value;
        const creditData = _.cloneDeep(credit);

        if (creditData.fields.application_type.value !== 'DO_NOT_APPLY_CASH') {
            let creditEntries = creditData.fields.credit_entries.value.map(entry => ({
                fields: {
                    ...entry.fields,
                    amount: { value: entry.fields.amount.value ?? 0 },
                    amount_tax_1: { value: entry.fields.amount_tax_1.value ?? 0 },
                    amount_tax_2: { value: entry.fields.amount_tax_2.value ?? 0 },
                    amount_tax_3: { value: entry.fields.amount_tax_3.value ?? 0 },
                    amount_tax_4: { value: entry.fields.amount_tax_4.value ?? 0 }
                }
            }));

            creditData.fields.credit_entries.value = creditEntries
        }

        try {
            creditId = await this.createCredit(creditData, applicationDate);

        } catch (errors) {
            let errorMessage = "An error occurred while saving the credit:\n\n";

            errors.forEach(error => errorMessage += (error + "\n"));
            alert(errorMessage);

            this.setState({
                creditSaveInProgress: false,
            });
        }

        return window.location.href += `&credit=${creditId}`;
    }

    createCredit = async (credit, applicationDate) => {
        const applicationDateFormatted = applicationDate ? moment(applicationDate).format('YYYY-MM-DD') : null;

        const data = {
            ...credit.getQueryFieldValues(),
            application_date: applicationDateFormatted,
        };

        const response = await queryCreateCredit(data, creditQueryFields);
        if (response.errors) {
            throw response.errors.map(error => error.message);
        }

        return response.data.createCredit.id;
    }

    exitCurrentMode = async () => {
        try {
            const credit = await this.props.config.fetchCredit();
            if (credit.errors) {
                throw credit.errors.map(error => error.message);
            }

            this.setState({ credit });
        } catch (error) {
            alert(error)
        }

        this.setState({
            refundMode: false,
            increaseMode: false,
            writeOffMode: false,
        });
    }

    getCreditDate() {
        const { newMode } = this.props;
        const { credit } = this.state;
        const { facilityTimezone } = this.fieldConfiguration;

        if (newMode) {
            return new Date(getCurrentDateInTimezone(facilityTimezone));
        }

        const creditDate = credit.fields.created_at.value;
        return new Date(convertUnixMsToDateInTimezone(creditDate, facilityTimezone));
    }

    renderCreditActions() {
        const { creditSaveInProgress, credit } = this.state;

        const { newMode } = this.props;

        let disableSaveCredit = false;
        const balance = credit?.fields?.balance?.value;

        if (credit?.fields?.credit_entries) {
            const creditEntryFields = credit.fields?.credit_entries.value[0].fields;

            const gross_amount = (creditEntryFields.amount.value || 0) + (creditEntryFields.amount_tax_1.value || 0) +
                (creditEntryFields.amount_tax_2.value || 0) + (creditEntryFields.amount_tax_3.value || 0) + (creditEntryFields.amount_tax_4.value || 0)

            disableSaveCredit = (creditEntryFields.amount.value && creditEntryFields.amount.value < 0) ||
                (creditEntryFields.amount_tax_1.value && creditEntryFields.amount_tax_1.value < 0) ||
                (creditEntryFields.amount_tax_2.value && creditEntryFields.amount_tax_2.value < 0) ||
                (creditEntryFields.amount_tax_3.value && creditEntryFields.amount_tax_3.value < 0) ||
                (creditEntryFields.amount_tax_4.value && creditEntryFields.amount_tax_4.value < 0) ||
                (gross_amount <= 0) ?
                true : false;
        }

        const paymentType = credit?.fields.payment_type.value;
        const isCarryoverCredit = paymentType === 'Carryover Credit';

        return (
            <>
                {newMode && (
                    <div className="parkchirp--credit-panel--credit-actions-container">
                        <ActionButton
                            label="Save Credit"
                            classNames={["parkchirp--action"]}
                            inProgressState={creditSaveInProgress}
                            onClick={this.handleSaveCreditClick}
                            disabled={disableSaveCredit}
                        />
                    </div>
                )}
                <div className="parkchirp--credit-panel--credit-actions-container">
                    {featureFlags.CREDIT_UPDATES && !isCarryoverCredit && !newMode && balance > 0 && (
                        <button
                            type="button"
                            className="button button-primary parkchirp--action"
                            onClick={() => this.setState({ refundMode: true })}
                        >
                            Refund/Reverse Credit
                        </button>
                    )}
                    {!newMode && featureFlags.ACCOUNTING_CREDITS &&
                        <button
                            type='button'
                            className='button button-primary parkchirp--action'
                            onClick={() => this.setState({ increaseMode: true })}
                        >
                            Increase Credit
                        </button>
                    }
                    {!newMode && featureFlags.ACCOUNTING_CREDITS && balance > 0 &&
                        <button
                            type='button'
                            className='button button-primary parkchirp--action'
                            onClick={() => this.setState({ writeOffMode: true })}
                        >
                            Write Off Credit
                        </button>
                    }
                </div>
            </>
        );
    }

    renderCreditAttributes() {
        const { newMode } = this.props;
        const { credit } = this.state;

        return (
            <div className="parkchirp--credit-panel--credit-attributes-container">
                <div className="parkchirp--credit-panel--credit-attributes-block">
                    <div className="parkchirp--credit-panel--credit-attribute">
                        <Field label={!featureFlags.SUBSCRIPTION_NAME || newMode ? "Subscription" : "Sub. Name:"}
                            id="parkchirp--credit--subscription-id"
                            layout="columns"
                        >
                            <InputSelectField
                                inputId="parkchirp--credit--subscription-id"
                                options={this.fieldConfiguration.credit.subscription_id.options}
                                value={credit?.fields.subscription_id.value}
                                onChange={this.storeCreditData("subscription_id")}
                                editMode={(featureFlags.PAYOR_SEARCH && this.subscriptionId) ? false : newMode}
                            />
                        </Field>
                    </div>
                    {featureFlags.SUBSCRIPTION_NAME && (
                        <>
                            <div className="parkchirp--credit-panel--credit-attribute">
                                <Field
                                    label="Billing Name:"
                                    id="parkchirp--credit--billing-name"
                                    layout="columns"
                                >
                                    <InputTextField
                                        inputId="parkchirp--credit--billing-name"
                                        value={this.fieldConfiguration.credit.billingName}
                                        editMode={false}
                                    />
                                </Field>
                            </div>
                            <div className="parkchirp--credit-panel--credit-attribute">
                                <Field
                                    label="Primary Contact:"
                                    id="parkchirp--credit--primary-contact"
                                    layout="columns"
                                >
                                    <InputTextField
                                        inputId="parkchirp--credit--primary-contact"
                                        value={this.fieldConfiguration.credit.pcName}
                                        editMode={false}
                                    />
                                </Field>
                            </div>
                        </>
                    )}
                </div>
                <div className="parkchirp--credit-panel--credit-attributes-block">
                    {featureFlags.ACCOUNT_ID && !newMode &&
                        <div className="parkchirp--credit-panel--credit-attribute">
                            <Field
                                label="Account ID"
                                id="parkchirp--credit--account_id"
                                containerClasses={[
                                    "parkchirp--credit-panel--field-container--account_id",
                                ]}
                                layout="columns"
                            >
                                <InputTextField
                                    inputId="parkchirp--credit--account_id"
                                    value={this.fieldConfiguration.credit.accountId}
                                    editMode={false}
                                />
                            </Field>
                        </div>
                    }
                    <div className="parkchirp--credit-panel--credit-attribute">
                        <Field
                            label="Credit Number"
                            id="parkchirp--credit--readable-id"
                            containerClasses={[
                                "parkchirp--credit-panel--field-container--readable-id",
                            ]}
                            layout="columns"
                        >
                            <InputTextField
                                inputId="parkchirp--credit--readable-id"
                                value={
                                    newMode
                                        ? "Auto-generated when credit is saved."
                                        : credit?.fields.readable_id.value
                                }
                                editMode={false}
                            />
                        </Field>
                    </div>
                    <div className="parkchirp--credit-panel--credit-attribute">
                        <Field
                            label="Credit Date"
                            id="parkchirp--credit--created-at"
                            containerClasses={[
                                "parkchirp--credit-panel--field-container--created-at",
                            ]}
                            layout="columns"
                        >
                            <InputDateField
                                inputId="parkchirp--credit--created-at"
                                value={this.getCreditDate()}
                                editMode={false}
                            />
                        </Field>
                    </div>
                </div>
            </div>
        );
    }

    renderCreditRefundPanel() {
        const { credit, applicationDate } = this.state;

        return (
            <div className="parkchirp--invoice-panel--payment-panel-container">
                <CreditRefundPanel
                    credit={credit}
                    creditApplicationDate={applicationDate}
                    facilityId={this.props.config.facilityId}
                    onExit={this.exitCurrentMode}
                    fieldConfiguration={this.fieldConfiguration}
                />
            </div>
        );
    }

    renderCreditIncreasePanel() {
        return (
            <CreditIncreasePanel
                credit={this.state.credit}
                facilityId={this.props.config.facilityId}
                facilityTimezone={this.fieldConfiguration.facilityTimezone}
                onExit={this.exitCurrentMode}
            />
        );
    }

    renderCreditWriteOffPanel() {
        return (
            <CreditWriteOffPanel
                credit={this.state.credit}
                onExit={this.exitCurrentMode}
            />
        );
    }

    renderCreditTable() {
        const {
            credit,
            applicationDate,
        } = this.state;
        const { newMode } = this.props;

        return (
            <div className="parkchirp--invoice-panel--invoice-line-items-container">
                <Field layout="stacked">
                    <CreditLineItemPanel
                        credit={credit}
                        applicationDate={applicationDate}
                        newMode={newMode}
                        onChange={this.storeCreditData}
                        fieldConfiguration={this.fieldConfiguration}
                    />
                </Field>
            </div>
        );
    }

    renderCreditNotes() {
        const { credit } = this.state;
        const { newMode } = this.props;

        return (
            <div className="parkchirp--credit-panel--credit-attribute">
                <Field
                    label="Notes"
                    id="parkchirp--credit--notes"
                    containerClasses={[
                        "parkchirp--credit-detail--field-container--notes",
                    ]}
                    layout="stacked"
                >
                    <InputTextField
                        inputId="parkchirp--credit--notes"
                        value={credit?.fields.notes.value}
                        onChange={this.storeCreditData("notes")}
                        multiLine={true}
                        editMode={newMode}
                    />
                </Field>
            </div>
        );
    }

    render() {
        const {
            refundMode,
            increaseMode,
            writeOffMode,
        } = this.state;

        const renderCreditActions = !refundMode && !increaseMode && !writeOffMode;

        return (
            <>
                {this.renderCreditAttributes()}
                {refundMode && this.renderCreditRefundPanel()}
                {increaseMode && this.renderCreditIncreasePanel()}
                {writeOffMode && this.renderCreditWriteOffPanel()}
                {renderCreditActions && this.renderCreditActions()}
                {this.renderCreditTable()}
                {this.renderCreditNotes()}
            </>
        );
    }
}

export default CreditPanel;
